import { useEffect, useState } from 'react';
import type { ComponentType, JSX, ReactNode } from 'react';
import { AppContextProvider } from '@ndrc/app-sdk/client';
import type { AppContext } from '@ndrc/app-sdk/client';
import { findClientLoader } from './registry';
import { t } from '../messages';

/**
 * Charge le `client.tsx` d'une app depuis le registre **généré**, puis le monte avec son
 * `AppContext`.
 *
 * L'`import()` dynamique donne un fichier par app sans aucune configuration de découpe : c'est le
 * bundler qui sépare, pas nous. Le module chargé est mémorisé pour la vie de l'onglet — rouvrir
 * une app ne la retélécharge pas et ne la remonte pas à zéro.
 *
 * Une erreur de chargement (module absent, `client.tsx` qui n'exporte pas ce qu'il devrait, app
 * qui plante à l'import) est **relevée pendant le rendu**, où l'`AppErrorBoundary` qui entoure ce
 * composant l'attrape. C'est le seul chemin : rien n'est avalé en silence.
 */

interface AppClientShape {
  Student?: unknown;
}

const loaded = new Map<string, ComponentType>();
const inFlight = new Map<string, Promise<ComponentType>>();

function readStudentComponent(module: unknown): ComponentType {
  const record = module as Record<string, unknown> | null;
  // Deux formes acceptées, parce que les deux sont naturelles à écrire : `export default
  // defineAppClient({...})` et `export const client = defineAppClient({...})`.
  const candidate = (record?.['default'] ?? record?.['client']) as AppClientShape | undefined;
  const Student = candidate?.Student;
  if (typeof Student !== 'function') {
    throw new Error(
      "Le client.tsx de cette application n'exporte pas de composant « Student ». Attendu : " +
        'export default defineAppClient({ Student }).',
    );
  }
  return Student as ComponentType;
}

async function loadStudent(appId: string): Promise<ComponentType> {
  const loader = findClientLoader(appId);
  if (loader === undefined) throw new Error(t('host.unknownAppHint'));
  const Student = readStudentComponent(await loader());
  loaded.set(appId, Student);
  return Student;
}

function startLoading(appId: string): Promise<ComponentType> {
  const pending = inFlight.get(appId);
  if (pending !== undefined) return pending;
  const promise = loadStudent(appId).finally(() => inFlight.delete(appId));
  inFlight.set(appId, promise);
  return promise;
}

type MountState =
  | { status: 'loading' }
  | { status: 'ready'; Student: ComponentType }
  | { status: 'failed'; error: Error };

export interface StudentMountProps {
  appId: string;
  ctx: AppContext;
  /** Affiché tant que le fichier de l'app n'est pas arrivé. */
  fallback: ReactNode;
}

export function StudentMount({ appId, ctx, fallback }: StudentMountProps): JSX.Element {
  const [state, setState] = useState<MountState>(() => {
    const known = loaded.get(appId);
    return known === undefined ? { status: 'loading' } : { status: 'ready', Student: known };
  });

  useEffect(() => {
    if (loaded.has(appId)) return;
    let alive = true;
    void startLoading(appId).then(
      (Student) => {
        if (alive) setState({ status: 'ready', Student });
      },
      (error: unknown) => {
        if (alive) {
          setState({
            status: 'failed',
            error: error instanceof Error ? error : new Error(String(error)),
          });
        }
      },
    );
    return () => {
      alive = false;
    };
  }, [appId]);

  if (state.status === 'failed') throw state.error;
  if (state.status === 'loading') return <>{fallback}</>;

  const { Student } = state;
  return (
    <AppContextProvider value={ctx}>
      <Student />
    </AppContextProvider>
  );
}
